import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function About() {
  const [activeTab, setActiveTab] = useState<'intro' | 'reputation' | 'contact'>('intro');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotValue = formData.get('phone_alt') as string;
    if (honeypotValue && honeypotValue.trim()) {
      setFormSubmitting(false);
      setFormSubmitted(true);
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9nh2ae8mbnljin5a0jg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        setFormError(serverMsg.includes('spam') || serverMsg.includes('form data is spam') ? '提交失败，请检查输入内容' : serverMsg);
      }
    } catch {
      setFormError('网络错误，请稍后再试');
    } finally {
      setFormSubmitting(false);
    }
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '麻豆精品影视网',
    url: 'https://www.madoujipin.com/',
    description: '全网最全的高清影视平台，提供海量国产精品视频在线观看',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-10-8888-9999',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '建国路88号SOHO现代城A座18层',
      addressLocality: '北京市',
      addressRegion: '朝阳区',
      postalCode: '100022',
      addressCountry: 'CN',
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '21:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Saturday', 'Sunday'],
        opens: '10:00',
        closes: '18:00',
      },
    ],
  };

  const tabs = [
    { id: 'intro' as const, label: '品牌介绍', icon: 'ri-building-2-line' },
    { id: 'reputation' as const, label: '品牌口碑', icon: 'ri-thumb-up-line' },
    { id: 'contact' as const, label: '联系我们', icon: 'ri-customer-service-2-line' },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-10 md:pb-14 bg-background-50">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />

          <div className="mt-4 md:mt-6">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              关于我们
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              了解麻豆精品影视网的品牌故事、用户口碑与联系方式
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="mt-6 md:mt-8 flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-background-50 text-foreground-900'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={tab.icon}></i>
                </div>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Brand Introduction */}
          {activeTab === 'intro' && (
            <section className="mt-8 md:mt-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
                <div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                    品牌故事
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    麻豆精品影视网成立于2020年，是国内领先的高清影视在线观看平台。我们致力于为广大影迷提供海量精品影视资源，涵盖国产剧、古装剧、历史剧、电影、综艺等多种类型。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    作为91免费在线视频领域的标杆平台，我们始终坚持以用户体验为核心，不断优化播放技术和内容质量。平台所有影片均支持1080P及4K超高清画质，让每一位用户都能享受沉浸式的观影体验。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    经过多年的发展，麻豆精品影视网已积累了超过千万的注册用户，日活跃用户突破百万。我们的内容团队持续追踪最新影视动态，确保平台内容始终走在潮流前沿。
                  </p>

                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                    核心优势
                  </h2>
                  <ul className="space-y-3">
                    {[
                      { title: '海量资源', desc: '收录超过10万部影视作品，涵盖各大类型，满足不同用户的观影需求', icon: 'ri-database-2-line' },
                      { title: '高清画质', desc: '支持1080P至4K超高清画质，采用先进视频编码技术确保流畅播放', icon: 'ri-4k-line' },
                      { title: '极速加载', desc: '自建CDN加速网络，全国多节点覆盖，实现秒级缓冲播放体验', icon: 'ri-speed-up-line' },
                      { title: '免费观看', desc: '所有影片均可免费在线观看，无需付费会员即可畅享全部内容', icon: 'ri-vip-line' },
                    ].map((item) => (
                      <li key={item.title} className="flex gap-3">
                        <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 mt-0.5">
                          <i className={`${item.icon} text-primary-500 text-sm`}></i>
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-foreground-800">{item.title}</h3>
                          <p className="text-sm text-foreground-500 mt-0.5">{item.desc}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <div className="aspect-[4/3] rounded-lg overflow-hidden mb-6">
                    <img
                      src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20tech%20company%20office%20interior%20with%20young%20creative%20team%20working%2C%20warm%20lighting%2C%20open%20workspace%2C%20casual%20professional%20atmosphere%2C%20collaborative%20environment%2C%20modern%20furniture&width=1200&height=900&seq=about-brand-01&orientation=landscape"
                      alt="麻豆精品影视网办公室"
                      title="麻豆精品影视网 - 办公环境"
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-background-100 rounded-lg p-4 md:p-5 text-center">
                      <div className="font-heading text-3xl md:text-4xl font-bold text-primary-500">1000万+</div>
                      <p className="text-sm text-foreground-500 mt-1">注册用户</p>
                    </div>
                    <div className="bg-background-100 rounded-lg p-4 md:p-5 text-center">
                      <div className="font-heading text-3xl md:text-4xl font-bold text-accent-500">10万+</div>
                      <p className="text-sm text-foreground-500 mt-1">影视资源</p>
                    </div>
                    <div className="bg-background-100 rounded-lg p-4 md:p-5 text-center">
                      <div className="font-heading text-3xl md:text-4xl font-bold text-secondary-500">99.9%</div>
                      <p className="text-sm text-foreground-500 mt-1">服务可用率</p>
                    </div>
                    <div className="bg-background-100 rounded-lg p-4 md:p-5 text-center">
                      <div className="font-heading text-3xl md:text-4xl font-bold text-primary-500">4年</div>
                      <p className="text-sm text-foreground-500 mt-1">稳定运营</p>
                    </div>
                  </div>

                  <div className="aspect-[4/3] rounded-lg overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Chinese%20streaming%20video%20platform%20team%20discussion%20meeting%2C%20creative%20brainstorming%2C%20modern%20conference%20room%2C%20young%20professionals%2C%20warm%20interior%20lighting%2C%20collaborative%20vibe&width=1200&height=900&seq=about-brand-02&orientation=landscape"
                      alt="麻豆精品影视网团队"
                      title="麻豆精品影视网 - 团队协作"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Reputation */}
          {activeTab === 'reputation' && (
            <section className="mt-8 md:mt-10">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-10">
                <div className="bg-background-100 rounded-lg p-5 md:p-6 text-center">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-primary-100">
                    <i className="ri-star-fill text-primary-500 text-xl"></i>
                  </div>
                  <div className="font-heading text-3xl font-bold text-foreground-900">4.8</div>
                  <p className="text-sm text-foreground-500 mt-1">用户综合评分</p>
                </div>
                <div className="bg-background-100 rounded-lg p-5 md:p-6 text-center">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-accent-100">
                    <i className="ri-thumb-up-fill text-accent-500 text-xl"></i>
                  </div>
                  <div className="font-heading text-3xl font-bold text-foreground-900">96%</div>
                  <p className="text-sm text-foreground-500 mt-1">用户满意度</p>
                </div>
                <div className="bg-background-100 rounded-lg p-5 md:p-6 text-center">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-secondary-100">
                    <i className="ri-user-heart-line text-secondary-500 text-xl"></i>
                  </div>
                  <div className="font-heading text-3xl font-bold text-foreground-900">85%</div>
                  <p className="text-sm text-foreground-500 mt-1">用户回访率</p>
                </div>
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">
                用户评价
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                {[
                  {
                    name: '影迷小王',
                    rating: 5,
                    date: '2024-08-01',
                    comment: '用了两年多了，资源真的很全，画质也好。国产剧更新速度特别快，每次新剧上线第一时间就能看到，真心推荐！',
                  },
                  {
                    name: '追剧达人Lucy',
                    rating: 5,
                    date: '2024-07-28',
                    comment: '麻豆精品影视网是我用过最好用的免费视频网站，分类清晰，搜索方便，而且真的不卡顿。91免费在线视频首选平台！',
                  },
                  {
                    name: '电影爱好者老张',
                    rating: 4,
                    date: '2024-07-25',
                    comment: '电影资源更新及时，很多院线刚下映的片子就能在这里看到。4K画质非常清晰，在家就能享受影院级体验。',
                  },
                  {
                    name: '综艺控小美',
                    rating: 5,
                    date: '2024-07-20',
                    comment: '综艺栏目做得很用心，更新速度快，弹幕功能也很有趣。每周都会来看看有什么新综艺上线，已经成为日常习惯了。',
                  },
                  {
                    name: '古装剧迷阿强',
                    rating: 5,
                    date: '2024-07-15',
                    comment: '古装剧资源太丰富了！从经典老剧到最新热播剧都有，画质修复做得很好，老剧看起来也很清晰。强烈推荐！',
                  },
                  {
                    name: '科技宅小李',
                    rating: 4,
                    date: '2024-07-10',
                    comment: '作为一个技术控，很欣赏平台的播放体验。多线路切换、记忆播放、画质选择这些功能都很实用，开发团队很用心。',
                  },
                ].map((review, idx) => (
                  <div key={idx} className="bg-background-100 rounded-lg p-4 md:p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-primary-600 text-xs font-bold">{review.name[0]}</span>
                        </div>
                        <span className="text-sm font-medium text-foreground-800">{review.name}</span>
                      </div>
                      <span className="text-xs text-foreground-400">{review.date}</span>
                    </div>
                    <div className="flex items-center gap-0.5 mb-2">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className={`text-xs ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{review.comment}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Contact */}
          {activeTab === 'contact' && (
            <section className="mt-8 md:mt-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
                <div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                    联系方式
                  </h2>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100">
                        <i className="ri-map-pin-line text-primary-500"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-800">公司地址</h3>
                        <p className="text-sm text-foreground-500 mt-0.5">北京市朝阳区建国路88号SOHO现代城A座18层</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100">
                        <i className="ri-phone-line text-accent-500"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-800">客服电话</h3>
                        <p className="text-sm text-foreground-500 mt-0.5">010-8888-9999</p>
                        <p className="text-xs text-foreground-400 mt-0.5">工作日 9:00 - 21:00 | 周末 10:00 - 18:00</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-secondary-100">
                        <i className="ri-mail-line text-secondary-500"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-800">电子邮箱</h3>
                        <p className="text-sm text-foreground-500 mt-0.5">contact@madoujipin.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100">
                        <i className="ri-wechat-line text-primary-500"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-800">微信公众号</h3>
                        <p className="text-sm text-foreground-500 mt-0.5">麻豆精品影视</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                      办公时间
                    </h2>
                    <div className="bg-background-100 rounded-lg p-4 md:p-5 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-foreground-600">周一至周五</span>
                        <span className="text-foreground-800 font-medium">9:00 - 21:00</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-foreground-600">周六至周日</span>
                        <span className="text-foreground-800 font-medium">10:00 - 18:00</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-foreground-600">法定节假日</span>
                        <span className="text-foreground-800 font-medium">10:00 - 17:00</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                    在线留言
                  </h2>
                  
                  {formSubmitted ? (
                    <div className="bg-accent-100 rounded-lg p-6 text-center">
                      <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3">
                        <i className="ri-check-line text-3xl text-accent-600"></i>
                      </div>
                      <h3 className="text-base font-semibold text-accent-800">留言已提交</h3>
                      <p className="text-sm text-accent-600 mt-1">感谢您的留言，我们会尽快回复您！</p>
                      <button
                        onClick={() => setFormSubmitted(false)}
                        className="mt-4 px-5 py-2 bg-accent-500 text-background-50 text-sm font-medium rounded-md hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        继续留言
                      </button>
                    </div>
                  ) : (
                    <form
                      id="contact-form"
                      data-readdy-form="true"
                      onSubmit={handleFormSubmit}
                      className="bg-background-100 rounded-lg p-5 md:p-6 space-y-4"
                    >
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                        <input
                          id="contact-subject"
                          name="subject"
                          type="text"
                          required
                          className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入留言主题"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                          placeholder="请输入您的留言内容（最多500字）"
                        ></textarea>
                      </div>
                      <input
                        type="text"
                        name="phone_alt"
                        className="honeypot-field"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                      {formError && (
                        <p className="text-sm text-red-600">{formError}</p>
                      )}
                      <button
                        type="submit"
                        disabled={formSubmitting}
                        className="w-full px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-md hover:bg-primary-600 disabled:opacity-50 transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
                      >
                        {formSubmitting ? (
                          <><i className="ri-loader-4-line animate-spin mr-2"></i>提交中...</>
                        ) : (
                          <><i className="ri-send-plane-line mr-2"></i>提交留言</>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}